import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageDto } from '@asset-metrix/portal-gateway-client-js';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../store';
import { findTableConfigForEntity } from '../utils/pageConfigHelpers';

import { presetFilterSelectionForFilter } from './presetFilterSelectionForFilter';

export const TABLE_FILTER_URL_PARAM_PREFIX = 'tf_';

type ExtractedFilter = {
  [entityId: string]: {
    filterKey: string;
    searchParamKey: string;
    value: string[];
  }[];
};

export function createTableFilterSearchParamKey(
  widgetId: string,
  filterId: string
) {
  return `${TABLE_FILTER_URL_PARAM_PREFIX}${widgetId}_${filterId}`;
}

function extractFiltersFromUrlParam(
  existingFilters: ExtractedFilter,
  { key, value }: { key: string; value: string[] }
): ExtractedFilter {
  const [, entityId, ...filterKeySplit] = key.split('_');
  // Make sure filter keys can contain underscores
  const filterKey = filterKeySplit.join('_');

  existingFilters[entityId] = [
    ...(existingFilters[entityId] || []),
    { filterKey, searchParamKey: key, value },
  ];
  return existingFilters;
}

export function usePresetTableFiltersFromUrl(pageConfig?: PageDto) {
  const [searchParams] = useSearchParams();
  const [filterData, presetFilterSelection, setFilterInitialised] = useStore(
    useShallow((state) => [
      state.filterData,
      state.presetFilterSelection,
      state.setFilterInitialised,
    ])
  );

  const extractedFiltersPerEntity = useMemo(
    () =>
      [...searchParams.keys()]
        .filter((key) => key.startsWith(TABLE_FILTER_URL_PARAM_PREFIX))
        .map((key) => ({ key, value: searchParams.getAll(key) }))
        .reduce(extractFiltersFromUrlParam, {}),
    [searchParams]
  );

  useEffect(() => {
    if (!pageConfig) {
      return;
    }

    for (const entityId in extractedFiltersPerEntity) {
      const tableConfig = findTableConfigForEntity(
        pageConfig.contents,
        entityId
      );

      if (!tableConfig || filterData[entityId]?.meta.initialised) {
        continue;
      }

      for (const extractedFilter of extractedFiltersPerEntity[entityId]) {
        if (extractedFilter.value) {
          const filterConfig = tableConfig.filters?.find(
            (f) => f.key === extractedFilter.filterKey
          );

          if (
            filterConfig &&
            // Exclude global search filters as they are handled via global url parameter
            // c.f. usePresetFiltersFromUrl
            !(filterConfig.type === 'text' && filterConfig.isGlobalSearchFilter)
          ) {
            presetFilterSelectionForFilter(
              presetFilterSelection,
              entityId,
              extractedFilter.value,
              filterConfig
            );
          }
        }
      }

      setFilterInitialised(entityId);
    }
  }, [
    pageConfig,
    presetFilterSelection,
    extractedFiltersPerEntity,
    setFilterInitialised,
    filterData,
  ]);
}
